import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from '@kantera/ui';
import { theme, css, flexCenter, flexCol } from '@kantera/ui';

// ---------------------------------------------------------------------------
// Kantera brand mark — official low-poly ball glyph
// ---------------------------------------------------------------------------

const KanteraKMark = () => (
  <img
    src="/assets/brand/kantera-mark.png"
    alt="Kantera"
    width={40}
    height={40}
    style={{ objectFit: 'contain', display: 'block' }}
  />
);

// ---------------------------------------------------------------------------
// OrgSwitchOverlay
// ---------------------------------------------------------------------------

interface OrgSwitchOverlayProps {
  visible: boolean;
  orgName: string;
}

/**
 * Full-screen overlay rendered during org switch transitions.
 * Fades in when visible, fades out when dismissed.
 * Minimum 500ms display to prevent visual flicker on fast switches.
 */
export function OrgSwitchOverlay({ visible, orgName }: OrgSwitchOverlayProps) {
  // Track whether we should actually render the overlay.
  // We keep it rendered briefly after `visible` becomes false to allow the
  // fade-out animation to complete before removing from DOM.
  const [rendered, setRendered] = useState(visible);
  const [opacity, setOpacity] = useState(visible ? 1 : 0);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minDisplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minDisplayDoneRef = useRef(!visible);

  useEffect(() => {
    if (visible) {
      // Clear any pending hide timer from a previous close
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      minDisplayDoneRef.current = false;
      setRendered(true);
      // Allow one paint before fading in so the browser registers the
      // initial opacity: 0 and animates from there
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpacity(1));
      });
      // Start minimum display timer
      minDisplayTimerRef.current = setTimeout(() => {
        minDisplayDoneRef.current = true;
      }, 500);
    } else {
      // Respect minimum display time before fading out
      const hide = () => {
        setOpacity(0);
        // Remove from DOM after fade-out completes (150ms transition)
        hideTimerRef.current = setTimeout(() => setRendered(false), 200);
      };

      if (minDisplayDoneRef.current) {
        hide();
      } else {
        // Wait for remainder of minimum display period
        minDisplayTimerRef.current = setTimeout(hide, 500);
      }
    }

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (minDisplayTimerRef.current) clearTimeout(minDisplayTimerRef.current);
    };
  }, [visible]);

  if (!rendered) return null;

  const overlayStyle = css(
    {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: theme.zIndex.tooltip,
      backgroundColor: `rgba(8, 8, 13, 0.95)`, // bg.primary at 95% opacity
      opacity,
      transition: `opacity ${theme.transitions.fast}`,
    },
    flexCenter(),
  );

  const contentStyle = css(flexCol(theme.spacing.lg), {
    alignItems: 'center',
    textAlign: 'center',
  });

  const headingStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.base.size,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.colors.text.secondary,
    lineHeight: theme.typography.fontSize.base.lineHeight,
    margin: 0,
  };

  const orgNameStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text.primary,
  };

  return (
    <div
      style={overlayStyle}
      role="status"
      aria-live="polite"
      aria-label={`Switching to ${orgName}`}
    >
      <div style={contentStyle}>
        <KanteraKMark />
        <p style={headingStyle}>
          Switching to <span style={orgNameStyle}>{orgName}</span>
        </p>
        <Spinner size="md" />
      </div>
    </div>
  );
}
